// Render-only popover for the KPI drawer history row's Evidence column.
// Empty → em-dash + aria-label; 1+ links → "🔗 N link(s)" trigger that
// opens a popover listing each link as "{label}\n{hostname} · opens in
// new tab". Hostnames are surfaced to help spot bit.ly / typosquatted
// domains before the user clicks.
//
// URL safety is delegated to the shared `safeExternalHref` helper (the
// single authority across every evidence-link sink): only http(s) URLs
// render as clickable anchors; everything else — `javascript:`/`data:`
// schemes and malformed strings, including already-stored legacy data —
// renders as plain text (still counted + visible so reviewers see what
// was stored, but never a clickable href). No local URL logic here.
//
// Scope: display only. The accompanying input (cap 20, label rules) is
// `EvidenceLinksField` — do not duplicate it here.
//
// Deferred: flipping the popover above when near the
// viewport bottom. Not implemented — the drawer has fixed width and
// the evidence cell is small, so absolute-below is sufficient in
// practice. Revisit if/when EvidenceLinkDisplay is reused outside the
// drawer context.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from "react";
import { Paperclip } from "lucide-react";
import type { EvidenceLink } from "../../../../../types";
import { safeExternalHref } from "../../../../../utils/evidenceLinks";

export interface EvidenceLinkDisplayProps {
  links?: EvidenceLink[];
}

interface RenderableLink {
  label: string;
  url: string;
  hostnameLine: string;
  /** The href for a safe http(s) link, or null → render as plain text. */
  href: string | null;
}

function buildHostnameLine(url: string, href: string | null): string {
  if (href) {
    // Safe http(s) URL — surface the hostname so reviewers can spot
    // typosquatted / shortener domains before clicking.
    return `${new URL(url).hostname} · opens in new tab`;
  }
  // Unsafe / malformed — show the raw string (no "opens in new tab", because
  // it is not a clickable link) so the reviewer still sees what was stored.
  return url;
}

export default function EvidenceLinkDisplay({
  links,
}: EvidenceLinkDisplayProps) {
  const safeLinks: RenderableLink[] = (links ?? []).map((l) => {
    const href = safeExternalHref(l.url);
    return {
      label: l.label,
      url: l.url,
      hostnameLine: buildHostnameLine(l.url, href),
      href,
    };
  });

  const [open, setOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);

  // Focus the active menuitem whenever the index changes while open.
  useEffect(() => {
    if (!open) return;
    const el = itemRefs.current[focusIndex];
    if (el) el.focus();
  }, [open, focusIndex]);

  // Click-outside dismissal. Mirrors SupplierCombobox pattern.
  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e: globalThis.MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [open]);

  const handleTriggerClick = useCallback(() => {
    setFocusIndex(0);
    setOpen((prev) => !prev);
  }, []);

  const handleKey = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (!open) return;
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusIndex((i) => Math.min(i + 1, safeLinks.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusIndex((i) => Math.max(i - 1, 0));
      }
    },
    [open, safeLinks.length],
  );

  // Empty state — em-dash + aria-label so screen-readers announce the
  // semantic meaning rather than an opaque dash character.
  if (safeLinks.length === 0) {
    return (
      <span
        aria-label="No evidence attached"
        className="text-body-sm text-slate-400 dark:text-slate-500"
      >
        —
      </span>
    );
  }

  const count = safeLinks.length;
  const label = `${count} link${count === 1 ? "" : "s"}`;

  return (
    <div ref={containerRef} className="relative inline-block" onKeyDown={handleKey}>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleTriggerClick}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-caption text-slate-600 hover:bg-slate-100 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-300 dark:hover:bg-slate-700"
      >
        <Paperclip aria-hidden="true" className="h-3.5 w-3.5" />
        {label}
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Evidence links"
          className="absolute right-0 z-20 mt-1 min-w-[220px] max-w-[360px] rounded-lg border border-slate-200 bg-white p-1 shadow-soft-lg dark:shadow-soft-lg-dark dark:border-slate-700 dark:bg-slate-800"
        >
          {safeLinks.map((l, i) => {
            const itemClass =
              "block rounded-md px-2 py-1.5 hover:bg-slate-50 focus:bg-slate-50 focus:outline-hidden dark:hover:bg-slate-700 dark:focus:bg-slate-700";
            const body = (
              <>
                <div className="text-body-sm text-slate-900 dark:text-slate-100">
                  {l.label || "Attached evidence"}
                </div>
                <div className="text-caption text-slate-500 dark:text-slate-400">
                  {l.hostnameLine}
                </div>
              </>
            );
            // Stop the click from bubbling to the row click-handler in the
            // history table (which would open the amend modal), and close the
            // popover. Safe links also navigate via target="_blank".
            const handleItemClick = (e: MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              setOpen(false);
            };
            return l.href ? (
              <a
                key={`${l.url}-${i}`}
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                role="menuitem"
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleItemClick}
                className={itemClass}
              >
                {body}
              </a>
            ) : (
              // Unsafe / malformed URL — non-clickable plain-text menuitem.
              // tabIndex=-1 keeps it reachable by the arrow-key roving focus.
              <div
                key={`${l.url}-${i}`}
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                role="menuitem"
                tabIndex={-1}
                onClick={handleItemClick}
                className={itemClass}
              >
                {body}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
